import React, { useEffect, useState } from "react";
import { Table, Col, Image, Row, Popover, Modal, Divider, Tag, Checkbox } from "antd";
import { CardContainer } from "../../components/Card/CardContainer";
import {
  CheckOutlined,
  CloseOutlined,
  FileSearchOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { RangePicker } from "../../components/DatePicker/DatePicker";
import Input from "../../components/Input/Input";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import Text from "../../components/Text/Text";
import color from "../../resource/color";
import icons from "../../resource/icon";
import Select from "../../components/Select/Select";
import { AlignType, FixedType } from "rc-table/lib/interface";
import { FlexCol, FlexRow } from "../../components/Container/Container";
import {
  getOrders,
  getPdfPo,
  getPdfTicket,
  createOrderSSEUrl,
} from "../../datasource/OrderDatasourc";
import {
  OrderDeliveryDestKey,
  OrderPaymentMethodName,
  OrderStatusKey,
  ORDER_DELIVERY_DEST_METHOD_NAME,
  ORDER_PAYMENT_METHOD_NAME,
  ORDER_PAYMENT_STATUS,
  ORDER_STATUS,
} from "../../definitions/orderStatus";
import { useSSE } from "../../hook/useSSE";
import { numberFormatter, priceFormatter } from "../../utility/Formatter";
import { OrderEntity } from "../../entities/OrderEntity";
import { zoneDatasource } from "../../datasource/ZoneDatasource";
import { getOrderStatus } from "../../utility/OrderStatus";
import Permission, { checkPermission } from "../../components/Permission/Permission";
import { roleAtom } from "../../store/RoleAtom";
import { useRecoilValue } from "recoil";
import dayjs from "dayjs";
import Button from "../../components/Button/Button";
import { StatusModalDescription } from "./orderComponent/statusModal";
import { icon } from "../../resource";

const SLASH_DMY = "DD/MM/YYYY HH:mm";
const SummaryBox = ({
  title,
  value,
  icon,
  color,
}: {
  title: string;
  value: number;
  icon: string;
  color: string;
}) => {
  const textColor = color === "#FFE75D" ? "darkGold" : "white";
  return (
    <Col flex='20%'>
      <div
        style={{
          width: "100%",
          padding: "18px 22px",
          borderRadius: 20,
          backgroundColor: color,
          marginBottom: 16,
        }}
      >
        <FlexRow align='center' justify='space-between'>
          <Text level={1} color={textColor} fontSize={32} fontWeight={700}>
            {value}
          </Text>
          <img src={icon} style={{ height: 33 }} />
        </FlexRow>
        <div>
          <Text
            level={4}
            color={textColor}
            fontWeight={700}
            style={{
              display: "block",
              height: 29,
              width: 168,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Text>
        </div>
      </div>
    </Col>
  );
};

export const OrderList: React.FC = () => {
  const pageSize = 8;
  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const userCompany = JSON.parse(localStorage.getItem("company")!);
  const { company } = userProfile;

  const roleData = useRecoilValue(roleAtom);
  const isShowPrice = checkPermission(["orderList", "showPrice"], roleData);
  const isManage = checkPermission(["orderList", "view"], roleData);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [zone, setZone] = useState<{ label: string; value: string; key: string }[]>([]);
  const [keyword, setKeyword] = useState(searchParams.get("keyword") || "");
  const [statusFilter, setStatusFilter] = useState<string[] | undefined>(
    searchParams
      .get("statusFilter")
      ?.split(",")
      ?.filter((x) => Object.keys(ORDER_STATUS).includes(x)),
  );
  const [zoneFilter, setZoneFilter] = useState<string[] | undefined>(
    searchParams.get("zoneFilter")?.split(","),
  );
  const [dateFilter, setDateFilter] = useState<any>();
  const [page, setPage] = useState<number>(parseInt(searchParams.get("page") || "1"));
  const [isFlashSaleOrder, setIsFlashSaleOrder] = useState<any>(
    searchParams.get("isFlashSaleOrder")?.toString() === "true" ? true : false,
  );
  const [loading, setLoading] = useState(false);
  const [dataState, setDataState] = useState({
    data: [] as OrderEntity[],
    statusCount: {
      COMPANY_CANCEL_ORDER: 0,
      CONFIRM_ORDER: 0,
      DELIVERY_SUCCESS: 0,
      IN_DELIVERY: 0,
      OPEN_ORDER: 0,
      REJECT_ORDER: 0,
      SHOPAPP_CANCEL_ORDER: 0,
      WAIT_APPROVE_ORDER: 0,
      WAIT_CONFIRM_ORDER: 0,
      APPROVED_ORDER: 0,
      WAIT_APPROVE_OVER_CREDIT: 0,
      APPROVE_SALE_ORDER: 0,
    },
    count: 0,
  });
  const [useRealTime, setUseRealTime] = useState(true);
  const [hasInitialData, setHasInitialData] = useState(false);

  // สร้าง SSE URL พร้อม parameters
  const isOnlyConfirmOrder = !!statusFilter?.includes("CONFIRM_ORDER");
  const isOnlyApprovedOrder =
    statusFilter?.length === 1 && statusFilter?.includes("APPROVED_ORDER");

  const getSseStatus = () => {
    const base = [...(statusFilter || [])];

    // OLD LOGIC
    // if (isOnlyConfirmOrder) base.push("CONFIRM_ORDER", "APPROVED_ORDER");
    // if (statusFilter?.includes("APPROVED_ORDER") || statusFilter?.includes("APPROVE_SALE_ORDER"))
    //   base.push("APPROVED_ORDER", "APPROVE_SALE_ORDER");

    // NEW LOGIC
    base.forEach((status: string) => {
      const groupKey = userCompany.companyType === "Chemical" ? "group_icpl" : "group_default";
      const groupStatus = ORDER_STATUS[status as OrderStatusKey]?.[groupKey];
      if (groupStatus) {
        Object.entries(ORDER_STATUS).forEach(([key, val]) => {
          if (val[groupKey] === groupStatus && !base.includes(key)) {
            base.push(key);
          }
        });
      }
    });

    return base.length > (statusFilter?.length ?? 0) ? Array.from(new Set(base)) : statusFilter;
  };

  const getSseIsOverCredit = () => {
    const hasApprovedGroup =
      statusFilter?.includes("APPROVED_ORDER") || statusFilter?.includes("APPROVE_SALE_ORDER");
    if (hasApprovedGroup) return "ALL";
    if (isOnlyApprovedOrder) return "TRUE";
    if (isOnlyConfirmOrder && statusFilter?.length === 1) return "FALSE";
    return "ALL";
  };

  const sseParams = {
    company,
    search: keyword,
    status: getSseStatus(),
    customerZones: zoneFilter,
    page,
    take: pageSize,
    startDate: dateFilter
      ? dateFilter[0]
        ? dateFilter[0].format("YYYY-MM-DD")
        : undefined
      : searchParams.get("startDate")
        ? searchParams.get("startDate")
        : undefined,
    endDate: dateFilter
      ? dateFilter[1]
        ? dateFilter[1].format("YYYY-MM-DD")
        : undefined
      : searchParams.get("endDate")
        ? searchParams.get("endDate")
        : undefined,
    isOverCredit: getSseIsOverCredit(),
    isFlashSale: isFlashSaleOrder.toString(),
  };

  const sseUrl = createOrderSSEUrl(sseParams);
  // ใช้ SSE Hook
  const {
    isConnecting,
    isConnected,
    error: sseError,
  } = useSSE(sseUrl, {
    enabled: useRealTime,
    onMessage: (data) => {
      if (data.error) {
        console.error("Order SSE Error:", data.error);
        return;
      }
      setDataState({
        data: data.data,
        statusCount: data.statusCount,
        count: data.count,
      });
      setHasInitialData(true);
      setLoading(false);
    },
    onError: (error) => {
      setLoading(false);
    },
    onOpen: () => {
      console.log("✅ Order SSE Connected");
    },
  });
  const [statusModalVisible, showStatusModal] = useState<any>(undefined);
  const [statusModalData, setStatusModalData] = useState<any>(null);

  // รีเซ็ต hasInitialData เมื่อเปลี่ยน parameters
  useEffect(() => {
    if (useRealTime) {
      setHasInitialData(false);
      setLoading(true);
    }
  }, [keyword, statusFilter, zoneFilter, dateFilter, page, useRealTime]);

  const isStatusModalAvailable = (orderStatus: OrderStatusKey) => {
    const availableStatus = [
      "WAIT_APPROVE_ORDER",
      // "WAIT_CONFIRM_ORDER",
      "WAIT_APPROVE_OVER_CREDIT",
      "WAIT_APPROVE_SALE_ORDER",
    ];
    return availableStatus.includes(orderStatus);
  };

  const clearStatusModal = () => {
    setStatusModalData(null);
    showStatusModal(undefined);
  };

  useEffect(() => {
    if (statusModalVisible) {
      let detail;
      if (statusModalVisible?.status === "WAIT_APPROVE_ORDER") {
        detail = {};
      } else if (statusModalVisible?.status === "WAIT_APPROVE_OVER_CREDIT") {
        detail = statusModalVisible?.orderCreditApprovers;
      } else if (statusModalVisible?.status === "WAIT_APPROVE_SALE_ORDER") {
        detail = statusModalVisible?.orderSoApprovers;
      } else {
        setStatusModalData(null);
        return;
      }

      setStatusModalData(detail);
    }
  }, [statusModalVisible]);

  const setParams = (sD?: any, eD?: any) => {
    let sd = "",
      sm = "",
      sy = "",
      ed = "",
      em = "",
      ey = "";
    sd = sD && sD?.date();
    sm = sD && sD?.month() + 1;
    sy = sD && sD?.year();

    ed = eD && eD?.date();
    em = eD && eD?.month() + 1;
    ey = ed && eD?.year();

    setSearchParams({
      keyword,
      page: `${page}`,
      statusFilter: (statusFilter || "").toString(),
      zoneFilter: (zoneFilter || "").toString(),
      startDate: sd && sm && sy ? `${sy}-${sm}-${sd}` : "",
      endDate: ed && em && ey ? `${ey}-${em}-${ed}` : "",
      isFlashSaleOrder: isFlashSaleOrder.toString(),
    });
    // save params to localStorage

    localStorage.setItem(
      "orderListParams",
      JSON.stringify({
        keyword,
        page,
        statusFilter,
        zoneFilter,
        startDate: sd && sm && sy ? `${sy}-${sm}-${sd}` : "",
        endDate: ed && em && ey ? `${ey}-${em}-${ed}` : "",
        isFlashSaleOrder: isFlashSaleOrder.toString(),
      }),
    );
  };

  useEffect(() => {
    localStorage.removeItem("orderListParams");
    // ถ้าไม่ใช้ real-time mode ให้ใช้ interval polling
    if (!useRealTime) {
      const interval = 3 * 60 * 1000;
      const id = setInterval(fetchData, interval);
      return () => clearInterval(id);
    }
  }, [useRealTime]);

  useEffect(() => {
    if (!!searchParams.get("startDate") || !!searchParams.get("endDate")) {
      const sd = searchParams.get("startDate") || undefined;
      const ed = searchParams.get("endDate") || undefined;
      setDateFilter([dayjs(sd), dayjs(ed)]);
      setParams(sd && dayjs(sd), ed && dayjs(ed));
    } else {
      setParams();
    }
  }, [keyword, statusFilter, zoneFilter, page, isFlashSaleOrder]);

  useEffect(() => {
    // ถ้าใช้ SSE ให้ตั้ง loading เป็น true เพื่อรอข้อมูลจาก SSE
    if (useRealTime) {
      if (!zone || zone.length <= 0) {
        getZoneByCompany();
      }

      const shouldShowLoading = isConnecting || (isConnected && !hasInitialData);
      setLoading(shouldShowLoading);
    } else {
      if (!loading) fetchData();
    }
  }, [
    page,
    keyword,
    statusFilter,
    zoneFilter,
    dateFilter,
    useRealTime,
    isConnecting,
    isConnected,
    hasInitialData,
  ]);

  const resetPage = () => setPage(1);

  const getZoneByCompany = async () => {
    const res = await zoneDatasource.getAllZoneByCompany(company);
    const data = res.map((item: any) => {
      return {
        label: item.zoneName,
        value: item.zoneName,
        key: item.zoneId,
      };
    });
    setZone(data);

    const newZoneFilter = zoneFilter?.filter((x) =>
      res.find((item) => `${item.zoneName}` === `${x}`),
    );
    if (newZoneFilter?.length !== zoneFilter?.length) setZoneFilter(newZoneFilter);
  };

  const fetchData = async () => {
    if (!zone || zone.length <= 0) {
      getZoneByCompany();
    }
    // ถ้าใช้ SSE และเชื่อมต่อไดแล้ว ไม่ต้อง fetch ข้อมูลเอง
    if (useRealTime && isConnected) {
      return;
    }
    try {
      const localParams =
        localStorage.getItem("orderListParams") &&
        JSON.parse(localStorage.getItem("orderListParams") || "");

      const allStatus = [...(localParams?.statusFilter || statusFilter)];
      allStatus.forEach((status: string) => {
        const groupKey = userCompany.companyType === "Chemical" ? "group_icpl" : "group_default";
        const groupStatus = ORDER_STATUS[status as OrderStatusKey]?.[groupKey];
        if (groupStatus) {
          Object.entries(ORDER_STATUS).forEach(([key, val]) => {
            if (val[groupKey] === groupStatus && !allStatus.includes(key)) {
              allStatus.push(key);
            }
          });
        }
      });
      const uniqueStatus = Array.from(new Set(allStatus));

      setLoading(true);
      const { data, statusCount, count } = await getOrders({
        company,
        search: localParams?.keyword || keyword,
        status: uniqueStatus,
        customerZones: localParams?.zoneFilter || zoneFilter,
        page,
        take: pageSize,
        startDate: dateFilter
          ? dateFilter[0]
            ? dateFilter[0].format("YYYY-MM-DD")
            : undefined
          : searchParams.get("startDate")
            ? searchParams.get("startDate")
            : undefined,
        endDate: dateFilter
          ? dateFilter[1]
            ? dateFilter[1].format("YYYY-MM-DD")
            : undefined
          : searchParams.get("endDate")
            ? searchParams.get("endDate")
            : undefined,
        isOverCredit: (() => {
          if (isOnlyApprovedOrder) return "TRUE";
          if (isOnlyConfirmOrder && statusFilter?.length === 1) return "FALSE";
          return "ALL";
        })(),
      });
      setDataState({ data, statusCount, count });
      setHasInitialData(true);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  // รีเซ็ต hasInitialData เมื่อสลับ mode
  useEffect(() => {
    if (!useRealTime) {
      setHasInitialData(false);
    }
  }, [useRealTime]);

  const getPdf = async (id: string, orderCode: string, company: string) => {
    setLoading(true);
    await getPdfPo(id, company).then((res: any) => {
      const blob = new Blob([res], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.setAttribute("download", `${orderCode}.pdf`);
      a.click();
      setLoading(false);
    });
  };

  const getTicketPdf = async (id: string, orderCode: string) => {
    setLoading(true);
    const updateBy =
      userProfile?.userNav || `${userProfile?.firstname} ${userProfile?.lastname}` || "-";
    await getPdfTicket(id, updateBy).then((res: any) => {
      const blob = new Blob([res], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.setAttribute("download", `Ticket_${orderCode}.pdf`);
      a.click();
      setLoading(false);
    });
  };

  const PageTitle = () => {
    return (
      <Row align='middle' gutter={16}>
        <Col className='gutter-row' span={14}>
          <div>
            <span
              className='card-label font-weight-bolder text-dark'
              style={{ fontSize: 20, fontWeight: "bold" }}
            >
              จัดการคำสั่งซื้อ
            </span>
          </div>
        </Col>
        {/* <Col span={2}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <Switch
              checked={useRealTime}
              onChange={(checked) => {
                setUseRealTime(checked);
                if (!checked) {
                  fetchData();
                }
              }}
              size='small'
            />
            <Text fontSize={12} color={isConnected ? "success" : sseError ? "error" : "Text3"}>
              {useRealTime
                ? isConnected
                  ? "🟢 Live"
                  : isConnecting
                    ? "🟡 Connecting..."
                    : "🔴 Offline"
                : "⚪ Manual"}
            </Text>
          </div>
        </Col> */}
        <Col span={8}></Col>
      </Row>
    );
  };

  const summaryBoxList =
    userCompany.companyType === "Chemical"
      ? [
          {
            color: "#FFE75D",
            icon: icons.hourglassColored,
            title: "รอร้านยืนยันคำสั่งซื้อ",
            value: dataState?.statusCount?.WAIT_CONFIRM_ORDER,
          },
          {
            color: "#E57300",
            icon: icons.waitApprovalCredit,
            title: "รออนุมัติวงเงิน",
            value: dataState?.statusCount?.WAIT_APPROVE_OVER_CREDIT,
          },
          {
            color: "#0068F4",
            icon: icons.iconCliboard,
            title: "อนุมัติคำสั่งซื้อแล้ว",
            value:
              dataState?.statusCount?.APPROVED_ORDER +
              dataState?.statusCount?.CONFIRM_ORDER +
              dataState?.statusCount?.APPROVE_SALE_ORDER,
          },
          {
            color: "#C1A100",
            icon: icons.iconTruck,
            title: "กำลังจัดส่ง",
            value: dataState?.statusCount?.IN_DELIVERY,
          },
          {
            color: "#2ED477",
            icon: icons.iconCheckedTruck,
            title: "รับสินค้าแล้ว",
            value: dataState?.statusCount?.DELIVERY_SUCCESS,
          },
        ]
      : [
          {
            color: "#E57300",
            icon: icons.waitApprovalCredit,
            title: "รอพิจารณาวงเงิน",
            value: dataState?.statusCount?.WAIT_APPROVE_OVER_CREDIT,
          },
          {
            color: "#0068F4",
            icon: icons.iconCliboard,
            title: "ยืนยันคำสั่งซื้อแล้ว",
            value:
              dataState?.statusCount?.APPROVED_ORDER +
              dataState?.statusCount?.CONFIRM_ORDER +
              dataState?.statusCount?.APPROVE_SALE_ORDER,
          },
          {
            color: "#A944CF",
            icon: icons.iconTruck,
            title: "รอรถเข้ารับสินค้า",
            value: dataState?.statusCount?.OPEN_ORDER,
          },
          {
            color: "#C1A100",
            icon: icons.prepareProduct,
            title: "รอขึ้นสินค้า",
            value: dataState?.statusCount?.IN_DELIVERY,
          },
          {
            color: "#26CC73",
            icon: icons.iconCheckedTruck,
            title: "ขึ้นสินค้าเรียบร้อยแล้ว",
            value: dataState?.statusCount?.DELIVERY_SUCCESS,
          },
        ];

  const columns = [
    {
      title: "รหัสคำสั่งซื้อ",
      dataIndex: "orderNo",
      key: "orderNo",
      width: 138,
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            {row.isFlashSale && (
              <Tag color={"#FFF7C9"} style={{ borderRadius: "4px" }}>
                <Text color='BK' level={6} fontWeight={700}>
                  <img src={icon.thor_flash_sale} style={{ width: "12px", height: "15px" }} /> Flash
                  Sale
                </Text>
              </Tag>
            )}
            <Text level={5}>{value || "-"}</Text>
            <Text level={6} color='Text3'>
              {moment(row.createAt).format(SLASH_DMY)}
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "รหัส SO",
      dataIndex: "soNo",
      key: "soNo",
      width: 132,
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <Text level={5}>{value || "-"}</Text>
          </FlexCol>
        );
      },
    },
    {
      title: "รายชื่อร้านค้า",
      dataIndex: "customerName",
      key: "customerName",
      width: 180,
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <Text level={5}>{value || "-"}</Text>
            <Text level={6} color='Text3'>
              {row.customerNo}
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "เขต",
      dataIndex: "customerZone",
      key: "customerZone",
      width: 70,
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <Text level={5}>{value || "-"}</Text>
          </FlexCol>
        );
      },
    },
    {
      title: "การจัดส่ง",
      dataIndex: "deliveryDest",
      key: "deliveryDest",
      width: 130,
      render: (value: OrderDeliveryDestKey, row: any, index: number) => {
        const mapTruckType: any = {
          DOUBLE_TRUCK: "รถพ่วง",
          SINGLE_TRUCK: "รถเดี่ยว",
        };
        return (
          <FlexCol>
            <Text level={6} fontWeight={700}>
              {mapTruckType[row?.truckType]} {row?.numberPlate || "-"}
            </Text>
            <Text level={6}>{value ? ORDER_DELIVERY_DEST_METHOD_NAME[value] : "-"}</Text>
          </FlexCol>
        );
      },
    },
    {
      title: "จัดเรียงสินค้า",
      dataIndex: "orderLoads",
      key: "orderLoads",
      width: 100,
      hidden: userCompany.companyCode.includes("ICPL"),
      render: (value: any, row: any, index: number) => {
        return (
          <FlexRow justify='center'>
            {row?.orderLoads.length ? (
              <div>
                <CheckOutlined style={{ color: color.success, fontSize: "18px" }} />
              </div>
            ) : (
              <div>
                <CloseOutlined style={{ color: color.error, fontSize: "18px" }} />
              </div>
            )}
          </FlexRow>
        );
      },
    },
    {
      title: "ราคารวม",
      dataIndex: "totalPrice",
      key: "totalPrice",
      width: 120,
      fixed: "right" as FixedType | undefined,
      render: (value: any, row: any, index: number) => {
        return (
          <FlexCol>
            <Text level={5}>{numberFormatter(value) || "-"}</Text>
            <Text level={6} color='Text3'>
              {ORDER_PAYMENT_METHOD_NAME[row.paymentMethod as OrderPaymentMethodName] || "-"}
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      width: 230,
      align: "start" as AlignType,
      fixed: "right" as FixedType | undefined,
      render: (value: OrderStatusKey, row: OrderEntity, index: number) => {
        const calPartialShipment =
          row?.orderProducts?.reduce((acc, curr: any) => {
            const qtyOutstanding = Number(curr.qtyOutstanding || "0");
            if (qtyOutstanding > 0) {
              return acc + qtyOutstanding;
            }
            return acc;
          }, 0) || 0;

        const calQtyShipment =
          row?.orderProducts?.reduce((acc, curr: any) => {
            const qtyShip = Number(curr.qtyShip || "0");
            if (qtyShip > 0) {
              return acc + qtyShip;
            }
            return acc;
          }, 0) || 0;

        return (
          <FlexCol align='start' justify='center'>
            <Row>
              <Text
                level={5}
                fontWeight={700}
                style={{
                  color: ORDER_STATUS[value]?.text_color,
                  backgroundColor: ORDER_STATUS[value]?.color,
                  padding: "3px 8px 2px 8px",
                  borderRadius: 4,
                  textAlign: "center",
                  fontSize: 14,
                }}
              >
                {getOrderStatus(value, company, row?.isOverCredit) || "-"}
              </Text>
              &nbsp;
              {calQtyShipment > 0 &&
                calPartialShipment > 0 &&
                !userCompany.companyType.includes("Fertilizer") && (
                  <Tag
                    style={{
                      borderRadius: 15,
                      borderColor: "#7A6B0F",
                      height: "5%",
                      padding: "2px 6px",
                    }}
                  >
                    <Text style={{ color: "#7A6B0F" }} fontSize={12} fontWeight={600}>
                      บางส่วน
                    </Text>
                  </Tag>
                )}
            </Row>

            {/* <Text
              level={6}
              style={{
                color: row?.paidStatus
                  ? ORDER_PAYMENT_STATUS[row?.paidStatus].name_default
                  : undefined,
                textAlign: "center",
              }}
            >
              {row?.paidStatus ? ORDER_PAYMENT_STATUS[row?.paidStatus].name_default : "-"}
            </Text> */}
            <Text level={6} color='Text3' style={{ textAlign: "center" }}>
              {moment(row.updateAt).format(SLASH_DMY)}
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "จัดการ",
      dataIndex: "action",
      key: "action",
      width: 140,
      hidden: !isManage,
      fixed: "right" as FixedType | undefined,
      render: (value: any, row: any, index: number) => {
        const canShowModal = isStatusModalAvailable(row.status);
        return {
          children: row.status ? (
            <Row justify={"start"} gutter={8}>
              <Col span={8}>
                <div
                  className='btn btn-icon btn-light btn-hover-primary btn-sm'
                  onClick={() => navigate("/order/" + row.orderId)}
                  // onClick={() =>
                  //   window.open(`${window.location.pathname}/${row.orderId}`, "_blank")
                  // }
                >
                  <span className='svg-icon svg-icon-primary svg-icon-2x'>
                    <UnorderedListOutlined style={{ color: color["primary"] }} />
                  </span>
                </div>
              </Col>
              <Col span={8}>
                <div
                  className='btn btn-icon btn-light btn-hover-primary btn-sm'
                  onClick={
                    canShowModal
                      ? () => {
                          showStatusModal(row);
                        }
                      : undefined
                  }
                >
                  <span className='svg-icon svg-icon-primary svg-icon-2x'>
                    <FileSearchOutlined
                      style={{ color: canShowModal ? color["primary"] : color["Text3"] }}
                    />
                  </span>
                </div>
              </Col>
              {userCompany.companyCode === "ICPF" && (
                <Col span={8}>
                  <div className='btn btn-icon btn-light btn-hover-primary btn-sm'>
                    <Image
                      onClick={() => getPdf(row?.orderId, row?.orderNo, userCompany.companyCode)}
                      src={icons.downloadFileIcon}
                      preview={false}
                      style={{ width: 18, height: 22, color: color.BK, paddingBottom: 4 }}
                    />
                  </div>
                </Col>
              )}
              {userCompany.companyCode === "ICPI" && (
                <Col span={8}>
                  <div className='btn btn-icon btn-light btn-hover-primary btn-sm'>
                    <Popover
                      placement='bottomRight'
                      content={contentDownloadPdf(row?.orderId, row?.orderNo)}
                    >
                      <Image
                        src={icons.downloadFileIcon}
                        preview={false}
                        style={{ width: 18, height: 22, color: color.BK, paddingBottom: 4 }}
                      />
                    </Popover>
                  </div>
                </Col>
              )}
            </Row>
          ) : (
            <>
              <div style={{ height: 32 }} />
            </>
          ),
        };
      },
    },
  ].filter((item) => !item.hidden);

  const contentDownloadPdf = (orderId: string, orderNo: string) => {
    return (
      <div>
        <div style={{ padding: "5px", cursor: "pointer", transition: "background-color 0.3s" }}>
          <a
            onClick={() => getPdf(orderId, orderNo, userCompany.companyCode)}
            onMouseEnter={(e) => (e.currentTarget.style.color = color.primary)}
            onMouseLeave={(e) => (e.currentTarget.style.color = color.black)}
          >
            ดาวน์โหลดลำดับจัดเรียงสินค้าขึ้นรถ
          </a>
        </div>
        <div style={{ padding: "5px", cursor: "pointer", transition: "background-color 0.3s" }}>
          <a
            onClick={() => getTicketPdf(orderId, orderNo)}
            onMouseEnter={(e) => (e.currentTarget.style.color = color.primary)}
            onMouseLeave={(e) => (e.currentTarget.style.color = color.black)}
          >
            ดาวน์โหลดใบเบิกจ่ายสินค้า (สำรอง)
          </a>
        </div>
      </div>
    );
  };

  return (
    <>
      <CardContainer>
        <PageTitle />
        <br />
        <br />
        <Row gutter={8}>
          {summaryBoxList.map((e, i) => (
            <SummaryBox {...e} key={i} />
          ))}
        </Row>
        <br />
        <Row justify='space-between' gutter={16}>
          <Col span={8}>
            <Input
              placeholder='ค้นหารหัสคำสั่งซื้อ / รหัส SO / ชื่อร้านค้า / รหัสร้านค้า'
              suffix={<SearchOutlined />}
              defaultValue={keyword}
              onPressEnter={(e) => {
                const value = (e.target as HTMLTextAreaElement).value;
                setKeyword(value);
                resetPage();
              }}
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value;
                if (!value) {
                  setKeyword("");
                  resetPage();
                }
              }}
            />
          </Col>
          <Col span={3}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Checkbox
                checked={isFlashSaleOrder as boolean}
                onChange={(e) => {
                  setIsFlashSaleOrder(Boolean(e.target.checked));
                  resetPage();
                }}
              >
                <Text>Flash Sale Order</Text>
              </Checkbox>
            </div>
          </Col>
          <Col span={5}>
            <RangePicker
              allowEmpty={[true, true]}
              enablePast
              value={dateFilter}
              onChange={(dates) => {
                if (!dates || (dates[0] && dates[1])) {
                  setParams(dates && dates[0], dates && dates[1]);
                  setDateFilter(dates);
                }
              }}
            />
          </Col>
          <Col span={4}>
            <Select
              data={Object.entries(ORDER_STATUS)
                .filter(
                  ([a]) =>
                    userCompany?.companyCode !== "ICPI" ||
                    !["WAIT_APPROVE_ORDER", "WAIT_CONFIRM_ORDER", "REJECT_ORDER"].includes(a),
                )
                .filter(([key, val]) => {
                  if (userCompany?.companyCode === "ICPL") {
                    return !val.group_icpl || val.group_icpl === key;
                  } else {
                    return !val.group_default || val.group_default === key;
                  }
                })
                .map(([key, val]) => ({
                  key,
                  value: key,
                  label: getOrderStatus(key, userCompany.companyCode),
                }))}
              // ladda อนุมัติ รวมกันสามอัน
              // มีแค่ยืนยัน แต่ไม่ต้องมีอนุมัติเลย CF, APPROVE_
              style={{ width: "100%" }}
              placeholder='สถานะทั้งหมด'
              mode='multiple'
              maxTagCount='responsive'
              showArrow
              value={statusFilter}
              onChange={(value: string[]) => {
                setStatusFilter(value);
                resetPage();
              }}
            />
          </Col>
          <Col span={4}>
            <Select
              data={zone}
              style={{ width: "100%" }}
              placeholder='เขตทั้งหมด'
              mode='multiple'
              maxTagCount='responsive'
              showArrow
              value={zoneFilter}
              onChange={(value: string[]) => {
                setZoneFilter(value);
                resetPage();
              }}
            />
          </Col>
        </Row>
        <br />
        {/* <Spin
          spinning={loadingPdf}
          delay={500}
          tip='Loading'
          size='large'
          style={{ padding: "10%" }}
        > */}
        <Table
          className='rounded-lg'
          columns={isShowPrice ? columns : columns.filter((x) => x.key !== "totalPrice")}
          dataSource={dataState.data.map((e: OrderEntity, i: number) => ({ ...e, key: i }))}
          pagination={{
            pageSize,
            current: page,
            total: dataState.count,
            showSizeChanger: false,
            position: ["bottomCenter"],
            onChange: (page) => {
              setPage(page);
            },
          }}
          size='large'
          tableLayout='fixed'
          loading={loading}
          scroll={{
            x: "max-content",
          }}
        />
        <Modal
          open={statusModalVisible !== undefined}
          onCancel={() => clearStatusModal()}
          footer={null}
          closable={false}
        >
          <FlexCol align='center'>
            <Text level={4} fontWeight={700}>
              เช็คสถานะ
              {statusModalVisible?.status === "WAIT_APPROVE_ORDER"
                ? "คำสั่งซื้อพิเศษ"
                : statusModalVisible?.status === "WAIT_APPROVE_OVER_CREDIT"
                  ? "วงเงินเกิน"
                  : statusModalVisible?.status === "WAIT_APPROVE_SALE_ORDER"
                    ? "ใบสั่งขาย"
                    : ""}
            </Text>
            <Text level={5}>รหัสคำสั่งซื้อ:&nbsp;{statusModalVisible?.orderNo} </Text>
            <Text level={5}>ชื่อร้านค้า:&nbsp;{statusModalVisible?.customerName} </Text>
          </FlexCol>
          <Divider />
          <StatusModalDescription
            status={statusModalVisible?.status}
            approvers={
              statusModalVisible?.status === "WAIT_APPROVE_ORDER"
                ? [
                    {
                      role: "SALE MANAGER (ผู้จัดการฝ่ายขาย)",
                      sequence: 1,
                      sequenceRequired: 1,
                      action: "PENDING",
                    },
                  ]
                : statusModalVisible?.status === "WAIT_APPROVE_SALE_ORDER"
                  ? statusModalVisible?.orderSoApprovers
                  : statusModalVisible?.status === "WAIT_APPROVE_OVER_CREDIT"
                    ? statusModalVisible?.orderCreditApprovers
                    : null
            }
          />
          <Divider />
          <Button title='ปิด' onClick={() => clearStatusModal()} />
        </Modal>
        {/* </Spin> */}
      </CardContainer>
    </>
  );
};
